import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackOnce } from "../lib/analytics";
import Honeypot from "./Honeypot";
import { Send } from "lucide-react";

const SUPPORT_OPTIONS = [
  "Emotional regulation",
  "Behaviour support",
  "Focus and attention",
  "Social skills",
  "Confidence building",
  "Following instructions",
  "Transitions / routines",
  "Autism Funding support",
  "Not sure yet",
];

const DIAGNOSIS_OPTIONS = [
  "No diagnosis",
  "ADHD",
  "Autism Spectrum",
  "Anxiety",
  "Behaviour challenges",
  "Developmental delay",
  "Other",
  "Prefer not to say",
];

const FUNDING_OPTIONS = ["Yes", "No", "Not sure yet"];

const CONTACT_METHODS = ["Phone", "Email", "Text message"];

export default function InquiryForm({
  source = "inquiry_form",
  sourcePage = "/",
  programSlug = "play-based-support",
  programType = "program",
  title = "Send us a message",
  subtitle = "Share a few details and a team member will get back to you shortly.",
  ctaLabel = "Request Information",
  variant = "card",
  hideHeader = false,
}) {
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    child_name: "",
    child_age: "",
    support_needs: [],
    diagnosis_or_support_need: "",
    autism_funding: "",
    preferred_contact_method: "",
    message: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const toggleSupport = (label) => {
    setForm((f) => ({
      ...f,
      support_needs: f.support_needs.includes(label)
        ? f.support_needs.filter((x) => x !== label)
        : [...f.support_needs, label],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.phone) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        parent_name: form.parent_name,
        email: form.email,
        phone: form.phone,
        child_name: form.child_name || undefined,
        child_age: form.child_age || undefined,
        program_slug: programSlug,
        program_type: programType,
        support_needs: form.support_needs,
        diagnosis_or_support_need: form.diagnosis_or_support_need || undefined,
        autism_funding: form.autism_funding || undefined,
        preferred_contact_method: form.preferred_contact_method || undefined,
        message: form.message || undefined,
        source_page: sourcePage,
        website: form.website,
      });
      setDone(true);
      toast.success("Message received — thank you.");
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wrapper =
    variant === "card"
      ? "rounded-[2rem] bg-white border border-slate-200/70 p-6 sm:p-8 shadow-sm"
      : "";

  if (done) {
    return (
      <div className={wrapper} data-testid="inquiry-form-success">
        <h3 className="font-display text-xl font-bold text-slate-900">
          Thank you for contacting Nexora.
        </h3>
        <p className="mt-3 text-slate-600 leading-relaxed text-sm">
          Our team will review your message and get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <div className={wrapper} data-testid="inquiry-form">
      {!hideHeader && title && (
        <div className="mb-5">
          <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        onFocusCapture={() =>
          trackOnce(`inquiry_started:${sourcePage}`, "inquiry_started", {
            source_page: sourcePage,
            program_slug: programSlug,
          })
        }
        className="grid gap-3.5"
      >
        <Honeypot value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
        <div className="grid sm:grid-cols-2 gap-3.5">
          <div>
            <label className="eyebrow text-slate-600 block mb-2">
              Parent / Guardian name *
            </label>
            <input
              required
              className="input-soft"
              value={form.parent_name}
              onChange={set("parent_name")}
              data-testid="inquiry-parent-name"
            />
          </div>
          <div>
            <label className="eyebrow text-slate-600 block mb-2">Phone *</label>
            <input
              required
              type="tel"
              className="input-soft"
              value={form.phone}
              onChange={set("phone")}
              data-testid="inquiry-phone"
            />
          </div>
        </div>
        <div>
          <label className="eyebrow text-slate-600 block mb-2">Email *</label>
          <input
            required
            type="email"
            className="input-soft"
            value={form.email}
            onChange={set("email")}
            data-testid="inquiry-email"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3.5">
          <div>
            <label className="eyebrow text-slate-600 block mb-2">
              Child's name
            </label>
            <input
              className="input-soft"
              value={form.child_name}
              onChange={set("child_name")}
              data-testid="inquiry-child-name"
            />
          </div>
          <div>
            <label className="eyebrow text-slate-600 block mb-2">
              Child's age
            </label>
            <input
              className="input-soft"
              value={form.child_age}
              onChange={set("child_age")}
              data-testid="inquiry-child-age"
            />
          </div>
        </div>

        <div>
          <label className="eyebrow text-slate-600 block mb-2">
            What type of support are you looking for?
          </label>
          <div
            className="grid sm:grid-cols-2 gap-2"
            data-testid="inquiry-support-needs"
          >
            {SUPPORT_OPTIONS.map((opt) => {
              const checked = form.support_needs.includes(opt);
              return (
                <label
                  key={opt}
                  className={`flex items-center gap-2.5 rounded-2xl border px-3.5 py-2.5 text-sm cursor-pointer transition-colors ${
                    checked
                      ? "border-[#0284C7] bg-sky-50 text-[#0369A1] font-semibold"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="accent-[#0284C7]"
                    checked={checked}
                    onChange={() => toggleSupport(opt)}
                    data-testid={`inquiry-support-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3.5">
          <div>
            <label className="eyebrow text-slate-600 block mb-2">
              Diagnosis or identified support need
            </label>
            <select
              className="input-soft appearance-none"
              value={form.diagnosis_or_support_need}
              onChange={set("diagnosis_or_support_need")}
              data-testid="inquiry-diagnosis"
            >
              <option value="">Select…</option>
              {DIAGNOSIS_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="eyebrow text-slate-600 block mb-2">
              Planning to use Autism Funding?
            </label>
            <select
              className="input-soft appearance-none"
              value={form.autism_funding}
              onChange={set("autism_funding")}
              data-testid="inquiry-autism-funding"
            >
              <option value="">Select…</option>
              {FUNDING_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="eyebrow text-slate-600 block mb-2">
            Preferred contact method
          </label>
          <select
            className="input-soft appearance-none"
            value={form.preferred_contact_method}
            onChange={set("preferred_contact_method")}
            data-testid="inquiry-contact-method"
          >
            <option value="">Select…</option>
            {CONTACT_METHODS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="eyebrow text-slate-600 block mb-2">
            Message / Additional information
          </label>
          <textarea
            rows={4}
            className="input-soft resize-none"
            placeholder="Share goals, questions, or anything else we should know."
            value={form.message}
            onChange={set("message")}
            data-testid="inquiry-message"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary mt-1 disabled:opacity-60"
          data-testid="inquiry-submit"
        >
          {loading ? "Sending…" : ctaLabel}
          <Send className="h-4 w-4" />
        </button>
        <p className="text-[11px] text-slate-400 leading-relaxed text-center mt-1">
          Your details are kept private. We only use them to reply to your
          inquiry.
        </p>
      </form>
    </div>
  );
}
